import {
  CommonActions,
  NavigationProp,
  RouteProp,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import { useResponsiveProp } from "@shopify/restyle";
import graphql from "babel-plugin-relay/macro";
import { add, format } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { ScrollView } from "react-native";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";

import { ErrorDisplayingFallback } from "@/features/ErrorHandling/components/ErrorDisplayingFallback/ErrorDisplayingFallback";
import { PlatformSpecificErrorBoundary } from "@/features/ErrorHandling/components/PlatformSpecificErrorBoundary";
import { EventsFilterModal } from "@/features/Events/components/EventsFilterModal";
import { EventsHeader } from "@/features/Events/components/EventsHeader";
import { EventsLayout } from "@/features/Events/components/EventsLayout";
import { NewPatronEventsList } from "@/features/Events/components/NewPatronEventsList/NewPatronEventsList";
import { NewPatronEventsSidebar } from "@/features/Events/components/NewPatronEventsSidebar/NewPatronEventsSidebar";
import { useClearQueryParam } from "@/features/Events/hooks/useClearQueryParam";
import { DateRange, DepartmentOrClubData } from "@/features/Events/types";
import { HomeTabsNavigatorParamList } from "@/navigators/HomeTabsNavigator/types";
import { Box } from "@whoosh/design-system/src/components/primitive";
import { useDebounce } from "@whoosh/utilities/src/hooks";

import { NewPatronEventsQuery } from "./__generated__/NewPatronEventsQuery.graphql";

type Props = {
  queryRef: PreloadedQuery<NewPatronEventsQuery>;
};

type EventsRouteParams = NonNullable<HomeTabsNavigatorParamList["Events"]>;

export const NewPatronEvents = ({ queryRef }: Props) => {
  const data = usePreloadedQuery(NewPatronEventsQueryString, queryRef);
  const club = data.session?.currentClientProfile?.member?.club ?? null;

  const route = useRoute<RouteProp<HomeTabsNavigatorParamList, "Events">>();
  const navigation =
    useNavigation<NavigationProp<HomeTabsNavigatorParamList, "Events">>();

  const isMobile = useResponsiveProp({ base: 1, md: 0 });

  const [search, setSearch] = useState(route.params?.search ?? "");
  const [tmpSearch, setTmpSearch] = useState(route.params?.search ?? "");

  const curDate = new Date();

  const initialStartDate = format(curDate, "yyyy-MM-dd");
  const initialEndDate = format(add(curDate, { months: 12 }), "yyyy-MM-dd");

  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: initialStartDate,
    endDate: initialEndDate,
  });

  const [tmpDateRange, setTmpDateRange] = useState<DateRange>({
    startDate: initialStartDate,
    endDate: initialEndDate,
  });

  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const [tmpSelectedTags, setTmpSelectedTags] = useState<string[]>([]);

  const handleSearchDebounce = useDebounce((value: string) => {
    setSearch(value);
  }, 400);

  const clearQueryParam = useClearQueryParam();

  const [currentDepartmentOrClubData, setCurrentDepartmentOrClubData] =
    useState<DepartmentOrClubData | null>({
      id: club?.id ?? null,
      slug: null,
    });

  const [tmpDepartmentOrClubData, setTmpDepartmentOrClubData] =
    useState<DepartmentOrClubData | null>({
      id: club?.id ?? null,
      slug: null,
    });

  const departmentOrClubId = currentDepartmentOrClubData?.id ?? club?.id ?? "";

  useEffect(() => {
    if (route.params?.departmentSlug && !currentDepartmentOrClubData?.slug) {
      const department = club?.departments?.edges?.find(
        (department) => department?.node?.slug === route.params?.departmentSlug
      );

      setCurrentDepartmentOrClubData({
        id: department?.node?.id ?? null,
        slug: department?.node?.slug ?? null,
      });

      setTmpDepartmentOrClubData({
        id: department?.node?.id ?? null,
        slug: department?.node?.slug ?? null,
      });
    }
  }, [
    route.params?.departmentSlug,
    club?.id,
    currentDepartmentOrClubData?.slug,
    club?.departments?.edges,
  ]);

  const setQueryParams = useCallback(
    (committedSearch: string) => {
      const params: Partial<EventsRouteParams> = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        search: committedSearch || undefined,
        departmentSlug: currentDepartmentOrClubData?.slug ?? undefined,
        tags: selectedTags.length > 0 ? selectedTags.join(",") : undefined,
      };

      navigation.dispatch({
        ...CommonActions.setParams(params),
        source: route.key,
      });
    },
    [
      currentDepartmentOrClubData?.slug,
      dateRange.endDate,
      dateRange.startDate,
      navigation,
      route.key,
      selectedTags,
    ]
  );

  useEffect(() => {
    /// Set search query param
    const paramSearch = route.params?.search;
    const isParamSearchBlank = paramSearch === "" || paramSearch === undefined;
    const isSearchBlank = search === "" || search === undefined;

    if (!!search && route.params?.search !== search) {
      setQueryParams(search);
    }

    // Clear search query param if search is empty
    if (isSearchBlank && !isParamSearchBlank) {
      clearQueryParam("search");
    }
  }, [search, setQueryParams, route.params?.search, clearQueryParam]);

  const showClearFilters =
    currentDepartmentOrClubData?.id?.includes("Department") ||
    tmpSearch !== "" ||
    dateRange.startDate !== initialStartDate ||
    dateRange.endDate !== initialEndDate ||
    selectedTags.length > 0;

  const clearFilters = () => {
    setCurrentDepartmentOrClubData({
      id: club?.id ?? null,
      slug: null,
    });
    setTmpSearch("");
    setSearch("");
    setDateRange({
      startDate: initialStartDate,
      endDate: initialEndDate,
    });
    setTmpDepartmentOrClubData({
      id: club?.id ?? null,
      slug: null,
    });
    setTmpDateRange({
      startDate: initialStartDate,
      endDate: initialEndDate,
    });
    setSelectedTags([]);
    setTmpSelectedTags([]);
  };

  const handleSearchChange = (data: string) => {
    setTmpSearch(data);

    if (isMobile) return;

    handleSearchDebounce(data);
  };

  const handleDateRangeChange = (data: DateRange) => {
    if (isMobile) {
      setTmpDateRange(data);
    } else {
      setDateRange(data);
    }
  };

  const handleDepartmentOrClubChange = (data: DepartmentOrClubData) => {
    if (isMobile) {
      setTmpDepartmentOrClubData(data);
    } else {
      setCurrentDepartmentOrClubData(data);
    }
  };

  const handleTagChange = (data: string[]) => {
    if (isMobile) {
      setTmpSelectedTags(data);
    } else {
      setSelectedTags(data);
    }

    if (data.length === 0) {
      clearQueryParam("tags");
    }
  };

  const confirmFilters = () => {
    setCurrentDepartmentOrClubData(tmpDepartmentOrClubData);
    setSearch(tmpSearch);
    setDateRange(tmpDateRange);
    setIsFilterModalOpen(false);
    setSelectedTags(tmpSelectedTags);
  };

  const shouldConfirmFilters =
    isMobile &&
    (tmpDateRange.startDate !== dateRange.startDate ||
      tmpDateRange.endDate !== dateRange.endDate ||
      tmpDepartmentOrClubData?.id !== currentDepartmentOrClubData?.id ||
      tmpDepartmentOrClubData?.slug !== currentDepartmentOrClubData?.slug ||
      tmpSelectedTags !== selectedTags);

  const shouldShowSearchCta = !!isMobile && tmpSearch !== search;

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const currentDepartmentName =
    data.session?.currentClientProfile?.member?.club?.departments?.edges?.find(
      (department) => department?.node?.id === currentDepartmentOrClubData?.id
    )?.node?.name;

  const clearFiltersProp =
    !shouldShowSearchCta && !shouldConfirmFilters && showClearFilters
      ? clearFilters
      : undefined;

  const isAnonymousCustomer =
    data.session?.currentClientProfile?.member?.__typename ===
    "AnonymousCustomer";

  return (
    <Box flex={1} bg="surface-primary">
      <EventsLayout
        shouldCenter
        header={
          <>
            {club ? (
              <EventsHeader
                isAnonymousCustomer={isAnonymousCustomer}
                isRounded={false}
                isScrolled={false}
                onBackPress={
                  isAnonymousCustomer
                    ? undefined
                    : () => {
                        navigation.goBack();
                      }
                }
                clubRef={club}
                openFilterModal={() => setIsFilterModalOpen(true)}
                currentDepartmentName={currentDepartmentName ?? null}
              />
            ) : null}
          </>
        }
        eventsList={
          <PlatformSpecificErrorBoundary
            fallback={(eventId, error, resetError) => (
              <ErrorDisplayingFallback
                error={error}
                eventId={eventId}
                resetError={resetError}
              />
            )}
            children={
              <NewPatronEventsList
                memberId={data.session?.currentClientProfile?.member?.id}
                departmentOrClubId={departmentOrClubId}
                dateRange={dateRange}
                search={search}
                selectedTags={selectedTags}
              />
            }
          />
        }
        sidebarContent={
          <ScrollView contentContainerStyle={{ flex: 1 }}>
            <NewPatronEventsSidebar
              fragmentRef={club}
              currentDepartmentOrClubData={currentDepartmentOrClubData}
              setCurrentDepartmentOrClubData={handleDepartmentOrClubChange}
              tmpSearch={tmpSearch}
              handleSearchChange={handleSearchChange}
              dateRange={dateRange}
              setDateRange={handleDateRangeChange}
              clearFilters={showClearFilters ? clearFilters : undefined}
              confirmFilters={shouldConfirmFilters ? confirmFilters : undefined}
              shouldShowSearchCta={shouldShowSearchCta}
              handleSearchPress={() => {
                setSearch(tmpSearch);
              }}
              selectedTags={isMobile ? tmpSelectedTags : selectedTags}
              setSelectedTags={handleTagChange}
            />
          </ScrollView>
        }
      />

      <EventsFilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        clubRef={club}
        fragmentRef={club}
        currentDepartmentOrClubData={
          isMobile ? tmpDepartmentOrClubData : currentDepartmentOrClubData
        }
        setCurrentDepartmentOrClubData={handleDepartmentOrClubChange}
        tmpSearch={tmpSearch}
        handleSearchChange={handleSearchChange}
        dateRange={isMobile ? tmpDateRange : dateRange}
        setDateRange={handleDateRangeChange}
        clearFilters={clearFiltersProp}
        confirmFilters={shouldConfirmFilters ? confirmFilters : undefined}
        shouldShowSearchCta={shouldShowSearchCta}
        handleSearchPress={() => {
          setSearch(tmpSearch);
          setIsFilterModalOpen(false);
        }}
        selectedTags={isMobile ? tmpSelectedTags : selectedTags}
        setSelectedTags={handleTagChange}
      />
    </Box>
  );
};

export const NewPatronEventsQueryString = graphql`
  query NewPatronEventsQuery {
    session {
      currentClientProfile {
        member {
          __typename
          ... on Member {
            id
          }
          ... on Customer {
            id
          }
          club {
            id
            ...NewPatronEventsSidebarFragment_club
            ...EventsHeaderFragment_club
            ...ClubLogo_Club

            slug
            departments {
              count
              edges {
                node {
                  id
                  name
                  slug
                  type
                  anyFacilityEventsEnabled
                }
              }
            }
          }
        }
      }
    }
  }
`;
